import {CreateKeyHistoryRecordForm, KeyHistoryRecordForm} from "../../api/Model";
import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel, FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {EmployeeSelect} from "../../components/employee-select/EmployeeSelect";
import {
    fromLocationTypeEnum,
    Location,
    locationTypeOptions,
    toLocationTypeEnum
} from "../../components/location/KeyLocationShield";
import {inputChangeEvent, mapSetState, selectChange, SetState} from "../../util/FormHandlers";
import {KeyVaultSelect} from "../../components/key-vault-select/KeyVaultSelect";
import {CompanySelect} from "../../components/company-select/CompanySelect";
import {DateField} from "../../components/date-field/DateField";

export function CreateRecordForm({form, setForm}: {
    form: CreateKeyHistoryRecordForm,
    setForm: SetState<CreateKeyHistoryRecordForm>,
}) {
    const finishPreviousRecords = useMemo(
        () => ({
            finishPreviousRecords: form.finishPreviousRecords,
            setFinishPreviousRecords: mapSetState(setForm, 'finishPreviousRecords'),
        }),
        [setForm, form.finishPreviousRecords],
    );

    return <EditRecordFormInternal form={form} setForm={setForm} finishPreviousRecords={finishPreviousRecords}/>;
}

export function EditRecordForm({form, setForm}: {
    form: KeyHistoryRecordForm,
    setForm: SetState<KeyHistoryRecordForm>,
}) {
    return <EditRecordFormInternal form={form} setForm={setForm}/>;
}


function EditRecordFormInternal<Form extends KeyHistoryRecordForm>({form, setForm, finishPreviousRecords}: {
    form: Form,
    setForm: SetState<Form>,
    finishPreviousRecords?: {
        finishPreviousRecords: boolean,
        setFinishPreviousRecords: SetState<boolean>,
    }
}) {
    const [locationForm, setLocationForm] = useState(toLocationTypeEnum(form.location));

    const setters = useMemo(() => ({
        setFromDate: mapSetState(setForm, "from"),
        setToDate: mapSetState(setForm, "to"),
        setNotes: mapSetState(setForm, "notes"),
        setLocation: mapSetState(setForm, "location"),
        setKeyVault: mapSetState(setForm, 'vault'),
        setEmployee: mapSetState(setLocationForm, 'employee'),
        setGuestFullName: mapSetState(setLocationForm, 'guestFullName'),
        setGuestCompany: mapSetState(setLocationForm, 'guestCompany'),
        setExpectedReturnDate: mapSetState(setLocationForm, 'expectedReturnDate'),
    }), [setForm])

    useEffect(() => {
        const newLocation = fromLocationTypeEnum(
            locationForm.locationType,
            locationForm.employee,
            locationForm.guestFullName,
            locationForm.guestCompany,
            locationForm.expectedReturnDate,
        );
        if (newLocation) {
            setters.setLocation(newLocation);
        }
    }, [locationForm, setters]);

    const handlers = useMemo(() => ({
        handleNotesChange: inputChangeEvent(setters.setNotes),
        handleLocationChange: selectChange<string | undefined, typeof locationForm>(setLocationForm,
            (newLocation) => ({locationType: (newLocation || 'with-vault') as Location}),
        ),
        handleGuestFullNameChange: inputChangeEvent(setters.setGuestFullName),
    }), [setLocationForm, setters.setGuestFullName, setters.setNotes]);

    return (
        <Box sx={{mt: 2}}>
            <Grid container spacing={2} alignItems="center">
                <Grid size={{md: 6, xs: 12}}>
                    <FormControl fullWidth margin="dense">
                        <DateField
                            label="Since"
                            value={form.from}
                            setValue={setters.setFromDate}
                            slotProps={{
                                field: {
                                    clearable: true,
                                },
                                textField: {
                                    id: "from-date",
                                    fullWidth: true,
                                    required: finishPreviousRecords?.finishPreviousRecords,
                                    variant: 'outlined',
                                },
                            }}
                        />
                    </FormControl>
                </Grid>
                {!finishPreviousRecords?.finishPreviousRecords && (
                    <Grid size={{md: 6, xs: 12}}>
                        <FormControl fullWidth margin="dense">
                            <DateField
                                label="Until"
                                value={form.to}
                                setValue={setters.setToDate}
                                slotProps={{
                                    field: {
                                        clearable: true,
                                    },
                                    textField: {
                                        id: 'to-date',
                                        fullWidth: true,
                                        variant: 'outlined',
                                    },
                                }}
                            />
                        </FormControl>
                    </Grid>
                )}
            </Grid>
            {finishPreviousRecords && (
                <FormControl fullWidth margin="normal" sx={{mb: 2}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={finishPreviousRecords.finishPreviousRecords}
                                onChange={e => finishPreviousRecords.setFinishPreviousRecords(e.target.checked)}
                            />
                        }
                        label="Finish previous history records"
                    />
                    <FormHelperText>Finish the previous history record. Set its “Until” date to this “Since” date.</FormHelperText>
                </FormControl>
            )}
            <FormControl fullWidth margin="normal">
                <KeyVaultSelect
                    id="vault"
                    label="Vault"
                    keyVault={form.vault}
                    setKeyVault={setters.setKeyVault}
                />
            </FormControl>
            <FormControl fullWidth margin="normal">
                <InputLabel id="location-type-label">Location</InputLabel>
                <Select
                    labelId="location-label"
                    id="location"
                    value={locationForm.locationType}
                    label="Location"
                    onChange={handlers.handleLocationChange}
                >{(['with-vault', 'with-employee', 'with-guest', 'lost', 'damaged', 'deactivated'] as Location[]).map(
                    (type) => {
                        const locationTypeOption = locationTypeOptions[type];
                        return (
                            <MenuItem key={type} value={type}>
                                {`${locationTypeOption.emoji}  ${locationTypeOption.label}`}
                            </MenuItem>
                        );
                    }
                )}
                </Select>
            </FormControl>
            {locationForm.locationType === 'with-employee' && (
                <FormControl fullWidth margin="normal">
                    <EmployeeSelect
                        id="employee"
                        label="Employee"
                        required
                        employee={locationForm.employee}
                        setEmployee={setters.setEmployee}
                    />
                </FormControl>
            )}
            {locationForm.locationType === 'with-guest' && (
                <>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            id="guest-full-name"
                            label="Contractor Full Name"
                            type="text"
                            fullWidth
                            required
                            variant="outlined"
                            value={locationForm.guestFullName || ""}
                            onChange={handlers.handleGuestFullNameChange}
                        />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                        <CompanySelect
                            label="Company"
                            variant="outlined"
                            value={locationForm.guestCompany || ""}
                            setValue={setters.setGuestCompany}
                        />
                    </FormControl>
                </>
            )}
            {(locationForm.locationType === 'with-employee' || locationForm.locationType === 'with-guest') && (
                <FormControl fullWidth margin="normal">
                    <DateField
                        label="Expected Return Date"
                        value={locationForm.expectedReturnDate}
                        setValue={setters.setExpectedReturnDate}
                        slotProps={{
                            field: {
                                clearable: true,
                            },
                            textField: {
                                id: 'ext-to-date',
                                fullWidth: true,
                                variant: 'outlined',
                            },
                        }}
                    />
                </FormControl>
            )}
            <FormControl fullWidth margin="normal">
                <TextField
                    margin="normal"
                    id="notes"
                    label="Notes"
                    type="text"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    value={form.notes || ""}
                    onChange={handlers.handleNotesChange}
                />
            </FormControl>
        </Box>
    );
}
